import { useState, useEffect, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/lib/supabase";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import type { UserProfile, Empresa } from "@shared/schema";
import type { Session, User } from "@supabase/supabase-js";
import { clearDossierDrafts } from "@/components/dossies/dossie-form";
import { authenticatedFetch, SessionRequiredError } from "@/lib/queryClient";

type ProfileWithEmpresa = UserProfile & { empresa: Empresa };

interface AuthContextType {
  session: Session | null;
  user: User | null;
  profile: ProfileWithEmpresa | null;
  loading: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType>({
  session: null,
  user: null,
  profile: null,
  loading: true,
  logout: async () => {},
});

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const queryClient = useQueryClient();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
      setLoading(false);
      if (!session) {
        queryClient.clear();
      }
    });

    return () => subscription.unsubscribe();
  }, [queryClient]);

  const { data: profile, isLoading: profileLoading } = useQuery<ProfileWithEmpresa>({
    queryKey: ["/api/auth/me", session?.user?.id],
    queryFn: async () => {
      if (!session?.access_token) return null;
      let res: Response;
      try { res = await authenticatedFetch("/api/auth/me"); }
      catch (error) { if (error instanceof SessionRequiredError) return null; throw error; }
      if (!res.ok) return null;
      return res.json();
    },
    enabled: !!session?.access_token,
  });

  const finalLoading = loading || (!!session && profileLoading && profile === undefined);

  const logout = async () => {
    await supabase.auth.signOut();
    queryClient.clear();
    clearDossierDrafts(); // rascunhos de abertura guardam dados pessoais
  };

  return (
    <AuthContext.Provider value={{
      session,
      user: session?.user ?? null,
      profile: profile ?? null,
      loading: finalLoading,
      logout,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
